import { useClerk } from '@clerk/clerk-react';
import type { AppRole } from '@empower-safety/shared-types';
import { ShieldX, Smartphone } from 'lucide-react';
import type { ReactNode } from 'react';
import { canUseDashboard, useMe } from '../api/useMe.js';

// Public install URLs for the mobile app. The iOS app id comes from
// apps/mobile/eas.json's `submit.production.ios.ascAppId`; the Android package
// matches `apps/mobile/app.json`'s `expo.android.package`. Update both here
// only if those identifiers ever change.
const IOS_APP_URL = 'https://apps.apple.com/app/id6764714051';
const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=au.com.empowersafety.app';

// Wraps any subtree that requires a specific app_role. The monitoring dashboard only
// makes sense for monitors and admins; workers get redirected to a friendly screen rather
// than empty lists full of 403s.
//
// Two modes:
//   • Page-level gate (no `fallback`): unauthenticated/unauthorised users see a full-screen
//     "Denied" screen. This is what wraps the whole <Shell /> outlet.
//   • Inline gate (with `fallback`): used to hide buttons/sections without taking over
//     the page. Pass `fallback={null}` to silently omit, or a React node for a message.
//
// While /me is loading, page-level gates render a skeleton; inline gates render nothing
// so the surrounding page layout doesn't jump.
export function RoleGate({
  allow,
  children,
  fallback,
}: {
  allow: AppRole[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { data: me, isLoading, error } = useMe();
  const inline = fallback !== undefined;

  if (isLoading) {
    return inline ? null : <LoadingShell />;
  }

  if (error) {
    if (inline) return <>{fallback}</>;
    return (
      <Denied
        title="Couldn't load your profile"
        message={
          'The API rejected your session token. If you just signed in, try refreshing. ' +
          'If the problem persists, check that the API is reachable and your organisation exists.'
        }
      />
    );
  }

  // Page-level gate (no fallback prop) uses capability semantics: monitor OR
  // admin OR team_leader can access the dashboard at all. The legacy `allow`
  // list still drives inline gates (button-level checks) for now.
  if (!inline) {
    if (!canUseDashboard(me?.capabilities)) {
      // A worker without monitor / admin / leader status gets the friendly
      // "open the mobile app" screen, not a security-error denial — workers
      // landing here after accepting an invite is a normal flow.
      if (me?.capabilities?.worker) {
        return <WorkerWelcome displayName={me.display_name} />;
      }
      return (
        <Denied
          title="This dashboard is for monitoring staff"
          message={
            'Your account does not have monitor, admin, or team-leader access in the current ' +
            'organisation. Contact your administrator if you think this is a mistake.'
          }
        />
      );
    }
    return <>{children}</>;
  }

  // Inline gate — preserves the existing app_role semantics so button-level
  // RoleGate(allow=['admin']) call sites continue to work unchanged.
  if (!me?.app_role || !allow.includes(me.app_role)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

function WorkerWelcome({ displayName }: { displayName: string }) {
  const { signOut } = useClerk();
  const firstName = (displayName ?? '').trim().split(/\s+/)[0] ?? '';
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-surface-0 p-8">
      <div className="max-w-md rounded-lg border border-line bg-white p-8 text-center shadow-panel">
        <Smartphone className="mx-auto mb-4 h-10 w-10 text-brand-purple" strokeWidth={1.5} />
        <h2 className="mb-2 font-heading text-lg font-semibold">
          {firstName ? `You're all set, ${firstName}` : "You're all set"}
        </h2>
        <p className="mb-6 text-sm text-ink-faint">
          Empower Safety is a mobile app for field workers. Download it on your phone and sign in
          with the same email you used to accept your invitation.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <a
            href={IOS_APP_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="rounded-md bg-brand-purple px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            Download for iPhone
          </a>
          <a
            href={ANDROID_APP_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="rounded-md border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-surface-0"
          >
            Get it on Android
          </a>
        </div>
        <button
          type="button"
          onClick={() => signOut()}
          className="mt-6 text-xs text-ink-faint underline hover:text-ink"
        >
          Not you? Sign out
        </button>
      </div>
    </div>
  );
}

function LoadingShell() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-surface-0">
      <div className="font-heading text-sm uppercase tracking-widest text-brand-purple/60">
        loading…
      </div>
    </div>
  );
}

function Denied({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-surface-0 p-8">
      <div className="max-w-md rounded-lg border border-line bg-white p-8 text-center shadow-panel">
        <ShieldX className="mx-auto mb-4 h-10 w-10 text-red-500" strokeWidth={1.5} />
        <h2 className="mb-2 font-heading text-lg font-semibold">{title}</h2>
        <p className="text-sm text-ink-faint">{message}</p>
      </div>
    </div>
  );
}
